import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SupplierHeader from './components/Header';
import { 
  faFilter, 
  faBell, 
  faBox, 
  faFileInvoice, 
  faCreditCard, 
  faExclamationCircle,
  faChevronRight,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DefaultIMG from './images/default_notifications.png';
import Box1 from './images/orderDetails_icons/box_hourglass.png';

const NotificationItem = ({ notification, onPress }) => {
  // Determine icon and color based on notification type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'order':
        return { 
          icon: <FontAwesomeIcon icon={faBox} className="text-[#D64541]" />,
          color: 'bg-white'
        };
      case 'quotation':
        return { 
          icon: <FontAwesomeIcon icon={faBox} className="text-[#D64541]" />,
          color: 'bg-white'
        };
      case 'payment':
        return { 
          icon: <FontAwesomeIcon icon={faBox} className="text-[#D64541]" />,
          color: 'bg-white'
        };
      case 'alert':
        return { 
          icon: <FontAwesomeIcon icon={faBox} className="text-[#D64541]" />,
          color: 'bg-white'
        };
      default:
        return { 
          icon: <FontAwesomeIcon icon={faBell} className="text-[#D64541]" />,
          color: 'bg-white'
        };
    }
  };

  // Determine notification type based on trigger or metadata
  const getNotificationType = () => {
    if (notification.trigger === 'quotation_created') return 'quotation';
    if (notification.metadata?.quotation_status) return 'order';
    return 'default';
  };

  const notificationType = getNotificationType();
const { icon, color } = getNotificationIcon(notificationType); 

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
   <div 
  onClick={onPress}
  className={`
    flex items-center 
    px-4 py-3 
    cursor-pointer 
    transition-all 
    duration-200 
    hover:bg-gray-50 
    active:bg-gray-100 
    ${color}
  `}
>
  {/* Icon Container */}
  <div className="mr-4 w-10 h-10 flex items-center justify-center">
    <img 
      src={Box1} 
      alt="Notification Icon"
      className=' w-full h-full object-cover rounded-lg  bg-[#F5F5F5] shadow-sm' />
  </div>

  {/* Content Container */}
  <div className="flex-1 min-w-0">
    <div className="flex justify-between items-center space-x-2">
      <h3 className=" text-sm font-semibold text-gray-900 truncate max-w-[70%] ">
        #{notification?.metadata?.order_number}
      </h3>
      <span className="font-bold text-sm text-[#524D4D] tracking-tight ">
        ₹{notification?.metadata?.total_amount}
      </span>
    </div>
    
    <p className=" font-semibold text-sm text-[#524D4D] truncate max-w-full   leading-relaxed">
      {notification?.metadata?.order_status}
    </p>
  </div>

  {/* Unread Indicator */}
  {!notification.read && (
    <div className="
      w-2.5 h-2.5 bg-[#D64541] rounded-full ml-3 animate-pulse"></div>
  )}
</div>

  );
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return '';

  const notificationDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Check if dates are in the same day
  const isSameDay = (date1, date2) => 
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();

  // If today
  if (isSameDay(notificationDate, today)) {
    return 'Today';
  }

  // If yesterday
  if (isSameDay(notificationDate, yesterday)) {
    return 'Yesterday';
  }

  // For older dates
  return notificationDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};

function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('unread');
  const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    useEffect(() => {
      const userDetails = JSON.parse(sessionStorage.getItem('vendorData')) || JSON.parse(localStorage.getItem('vendorData'));
    setUserData(userDetails);
    }, []);

  const GetAllNotifications = async () => {
    try {
      // Parse vendorData from localStorage
      const vendorDataString = localStorage.getItem('vendorData');
      if (!vendorDataString) {
        console.error('No vendor data found in localStorage');
        setLoading(false);
        return;
      }

      const vendorData = JSON.parse(vendorDataString);
      const token = vendorData.token;

      // Fetch notifications
      const response = await fetch(
        `${process.env.REACT_APP_FORE_NOTIFICATIONS}get-notifications?user_id=${vendorData.id}&channel=push`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token,
          },
        }
      );

      const data = await response.json();
      console.log('Notifications data:', data);

      if (data.success && Array.isArray(data.notifications)) {
        // Sort notifications by most recent first
        const sortedNotifications = data.notifications.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );

        setNotifications(sortedNotifications);
      } else {
        console.warn('No valid notifications data received');
      }
    } catch (error) {
      console.error('Error fetching Notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetAllNotifications();
  }, []);

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return true;
  });

  const MarkNotificationAsRead = async (notificationId) => {
    try {
      // Fetch notifications
      const response = await fetch(
        `${process.env.REACT_APP_FORE_NOTIFICATIONS}mark-notification-as-read?id=${notificationId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      console.log('Notifications data:', data);

      if (data.success ) {
      } else {
        console.warn('Error occured while updating notification as READ');
      }
    } catch (error) {
      console.error('Error fetching Notifications:', error);
    } 
  };

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    if (filteredNotifications.length === 0) return {};

    return filteredNotifications.reduce((groups, notification) => {
      const dateKey = formatNotificationDate(notification.metadata?.order_date);
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      
      groups[dateKey].push(notification);
      
      return groups;
    }, {});
  }, [filteredNotifications]);

  // Handle notification press
  const handleNotificationPress = (notification) => {
        console.log('Notification pressed:', notification);
        MarkNotificationAsRead(notification?._id)
    if (notification.metadata?.order_id) {
      navigate('/supplier/orderdetails', {
        state: { 
          quotationId: notification.metadata.order_id,
          quotationNumber: notification.metadata.order_number,
          vendor_id: userData.id
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <SupplierHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#D64541]"></div>
        </div>
      </div>
    );
  }

 return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="sticky top-0 z-50 flex flex-row justify-start bg-white shadow-md px-4 py-5">
        <button onClick={() => window.history.back()}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-3 text-gray-500" />
        </button>
        <h1 className="text-xl font-semibold">Notifications</h1>
      </nav>

      {/* Filter Section */}
{/*       <div className="px-4 pt-4 pb-2">
        <div className="flex space-x-3">
          <button
            onClick={() => setFilter('all')}
            className={`
              px-4 py-2 
              rounded-full 
              text-sm 
              font-medium 
              transition-all 
              duration-300 
              ${filter === 'all' 
                ? 'bg-[#FEF4F4] text-[#F84C5C]' 
                : 'text-[#524D4D] hover:bg-gray-200'
              }
            `}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`
              px-4 py-2 
              rounded-full 
              text-sm 
              font-medium 
              transition-all 
              duration-300 
              ${filter === 'unread' 
                ? 'bg-[#FEF4F4] text-[#F84C5C]' 
                : ' text-[#524D4D] hover:bg-gray-200'
              }
              flex items-center
            `}
          >
            Unread
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="
                ml-2 
                bg-white 
                text-[#D64541] 
                rounded-full 
                px-2 
                py-0.5 
                text-xs 
                font-bold
              ">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </button>
        </div>
      </div> */}

      {/* Notifications List */}
        <div className="p-4">
        {Object.keys(groupedNotifications).length === 0 ? (
          <div className="text-center py-12 text-[#524D4D]">
            <img 
              src={DefaultIMG}
              className="w-full h-full mx-auto mb-4 text-gray-300" 
              alt="No notifications"
            />
            <p className="font-semibold text-lg">
              {filter === 'all' 
                ? 'You have no notifications' 
                : 'No unread notifications'}
            </p>
          </div>
        ) : (
          <div className="bg-white space-y-4">
            {Object.entries(groupedNotifications).map(([dateGroup, notifications]) => (
              <div key={dateGroup}>
                {/* Date Group Header */}
                <div className="
                  sticky 
                  top-0 
                  bg-white 
                  z-10 
                  py-2 
                ">
                  <h3 className="
                    text-md 
                    font-semibold 
                    text-[#524D4D] 
                    
                    tracking-wider
                  ">
                    {dateGroup}
                  </h3>
                </div>

                {/* Notifications for this date group */}
                <div>
                  {notifications.map(notification => (
                    <NotificationItem
                      key={notification._id}
                      notification={notification}
                      onPress={() => handleNotificationPress(notification)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;
